import * as React from 'react';
import { Component } from 'react';
import { History } from 'history';
import { Profile } from '../../../../../../models/profile';
import * as propz from 'propz';
import {
  changeEmail,
  changePhone,
  resendEmailCode,
  resendPhoneCode,
  getCustomerProfile,
  getCustomerTokensExist,
} from '../../../../../../services/customer/customer';
import { verifyEmail, verifyPhone } from '../../../../../../services/customer/confirm';
import { Button } from '../../../../../../components/Button/Button';
import { AppCustomer } from '../../../../AppOrders';
import { Loader } from '../../../../../../components/Loader/Loader';
import { SimpleModal } from '../../../../../../components/SimpleModal/SimpleModal';
import { ProfileEmailChangeForm } from './ProfileEmailChangeForm/ProfileEmailChangeForm';
import { ProfilePhoneChangeForm } from './ProfilePhoneChangeForm/ProfilePhoneChangeForm';
import { NOTIFICATION_CHANNELS_TYPE } from '../../../../../../consts/notificationChannels';
import { createCustomerProfileNotificationChannel } from '../../../../../../services/superadmin/customers';
import { getPhoneWithoutFirstZero } from '../../../../../../helpers/phone';

interface Props {
  customer: AppCustomer;
  profile: Profile;
  updatePhoneInProfile: (phone: string) => void;
  updateEmailInProfile: (email: string) => void;
  updateIsCustomerVerified: () => void;
  history: History;
}

interface State {
  customerProfile: any;
  emailToken: string;
  phoneToken: string;
  email: string;
  phone: string;
  tokensStatus: any;
  isPhoneVerified: boolean;
  isEmailVerified: boolean;
  isEmailChangeModalOpen: boolean;
  isPhoneChangeModalOpen: boolean;
  isInfoModalOpen: boolean;
  infoModalTitle: string;
  infoModalBody: string;
  isLoading: boolean;
  emailRetrySec?: number;
  phoneRetrySec?: number;
}

export class CustomerVerificationTab extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      customerProfile: undefined,
      emailToken: '',
      phoneToken: '',
      email: '',
      phone: '',
      tokensStatus: undefined,
      isPhoneVerified: undefined,
      isEmailVerified: undefined,
      isEmailChangeModalOpen: false,
      isPhoneChangeModalOpen: false,
      isInfoModalOpen: false,
      infoModalTitle: 'Info',
      infoModalBody: '',
      isLoading: false,
      emailRetrySec: undefined,
      phoneRetrySec: undefined,
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true,
    });

    this.setItems();
  }

  setItems() {
    const { customer } = this.props;

    this.setState({
      isLoading: true,
    });

    getCustomerTokensExist(customer)
      .then(tokensStatus => {
        this.setState({
          tokensStatus: tokensStatus,
        });
        return tokensStatus;
      })
      .then(tokensStatus => {
        getCustomerProfile(customer).then(profile => {
          const { verification, email, phone } = profile;
          const { status } = verification;
          const { sms: isPhoneVerified, email: isEmailVerified } = status;
          switch (true) {
            case isPhoneVerified && isEmailVerified:
              this.setState({
                customerProfile: profile,
                isPhoneVerified: isPhoneVerified,
                isEmailVerified: isEmailVerified,
                email,
                phone,
                isLoading: false,
              });
              break;
            case isPhoneVerified:
              this.setState({
                customerProfile: profile,
                isPhoneVerified: isPhoneVerified,
                email,
                phone,
                isLoading: false,
              });
              break;
            case isEmailVerified:
              this.setState({
                customerProfile: profile,
                isEmailVerified: isEmailVerified,
                email,
                phone,
                isLoading: false,
              });
              break;
            default:
              this.setState({
                customerProfile: profile,
                email,
                phone,
                isLoading: false,
              });
          }
        });
      });
  }

  normalizePayload = (res: any) => (res as any)?.data ?? res;

  getUserFriendlyError(payload: any, fallback: string) {
    const code = payload?.code;
    const msg = payload?.message;
    const retry = payload?.retryAfterSec;

    if (code === 'RATE_LIMIT') {
      return msg || `Too many attempts. Please wait ${retry ?? 30} seconds and try again.`;
    }

    if (code === 'TOO_SOON') {
      return msg || `Please wait ${retry ?? 30} seconds before trying again.`;
    }

    if (code === 'AUTO_BLOCKED') {
      return msg || `Temporarily blocked. Please try again later.`;
    }

    if (code === 'EMAIL_NOT_CONFIRMED') {
      return msg || 'Please verify your email first.';
    }

    if (code === 'FORBIDDEN') {
      return msg || 'Not allowed.';
    }

    return msg || fallback;
  }

  startCountdown = (key: 'emailRetrySec' | 'phoneRetrySec', sec: number) => {
    this.setState({ [key]: sec } as any);

    const interval = setInterval(() => {
      this.setState(prev => {
        const v = (prev as any)[key];

        if (!v || v <= 1) {
          clearInterval(interval);
          return { [key]: undefined } as any;
        }

        return { [key]: v - 1 } as any;
      });
    }, 1000);
  };

  startCountdownFromNextResend = (key: 'emailRetrySec' | 'phoneRetrySec', nextResendAt?: number) => {
    if (!nextResendAt) return;

    const sec = Math.max(1, Math.ceil((nextResendAt - Date.now()) / 1000));
    this.startCountdown(key, sec);
  };

  openInfoModal = (title: string, body: string) => {
    this.setState({
      isInfoModalOpen: true,
      infoModalTitle: title,
      infoModalBody: body,
    });
  };

  onOkInfoModalClick = () => {
    this.setState({ isInfoModalOpen: false });
  };

  renderInfoModal() {
    const { isInfoModalOpen, infoModalTitle, infoModalBody } = this.state;

    return (
      <SimpleModal
        isOpen={isInfoModalOpen}
        title={infoModalTitle}
        body={infoModalBody}
        buttonCancelText={'Ok'}
        onCloseClick={this.onOkInfoModalClick}
      />
    );
  }

  onEmailTokenChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    this.setState({ emailToken: value });
  };

  onPhoneTokenChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    this.setState({ phoneToken: value });
  };

  onVerifyEmailClick = () => {
    const { customer } = this.props;
    const { emailToken } = this.state;

    this.setState({ isLoading: true });

    verifyEmail(customer, emailToken)
      .then(res => {
        const payload = this.normalizePayload(res);

        if (payload?.isError) {
          this.openInfoModal('Error', this.getUserFriendlyError(payload, 'Could not verify email.'));
          this.setState({ isEmailVerified: false, isLoading: false });
          return;
        }

        this.setState({ isEmailVerified: Boolean(payload?.confirmed), isLoading: false });
        this.setItems();
      })
      .catch(err => {
        const serverPayload = err?.response?.data;
        this.openInfoModal('Error', this.getUserFriendlyError(serverPayload, 'Could not verify email.'));
        this.setState({ isEmailVerified: false, isLoading: false });
      });
  };

  onVerifyPhoneClick = () => {
    const { customer } = this.props;
    const { phoneToken } = this.state;

    this.setState({ isLoading: true });

    verifyPhone(customer, phoneToken)
      .then(res => {
        const payload = this.normalizePayload(res);

        if (payload?.isError) {
          this.openInfoModal('Error', this.getUserFriendlyError(payload, 'Could not verify phone.'));
          this.setState({ isPhoneVerified: false, isLoading: false });
          return;
        }

        this.setState({ isPhoneVerified: Boolean(payload?.confirmed), isLoading: false });
        this.setItems();
      })
      .catch(err => {
        const serverPayload = err?.response?.data;
        this.openInfoModal('Error', this.getUserFriendlyError(serverPayload, 'Could not verify phone.'));
        this.setState({ isPhoneVerified: false, isLoading: false });
      });
  };

  onChangeEmailClick = () => {
    this.setState({
      isEmailChangeModalOpen: true,
    });
  };

  onChangePhoneClick = () => {
    this.setState({
      isPhoneChangeModalOpen: true,
    });
  };

  onPhoneChangeFormCancelClick = () => {
    this.setState({
      isPhoneChangeModalOpen: false,
    });
  };

  onEmailChangeFormCancelClick = () => {
    this.setState({
      isEmailChangeModalOpen: false,
    });
  };

  onResendEmailCodeClick = () => {
    const { customer } = this.props;

    this.setState({ isLoading: true });

    resendEmailCode(customer)
      .then(res => {
        const payload = this.normalizePayload(res);

        if (payload?.isError) {
          this.openInfoModal('Info', this.getUserFriendlyError(payload, 'Could not resend email code.'));
        } else {
          this.openInfoModal('Info', 'Verification code sent.');
          this.startCountdownFromNextResend('emailRetrySec', payload?.nextResendAt);
        }

        this.setState({ isLoading: false });
        this.setItems();
      })
      .catch(err => {
        const serverPayload = err?.response?.data;
        this.openInfoModal('Info', this.getUserFriendlyError(serverPayload, 'Could not resend email code.'));
        this.setState({ isLoading: false });
      });
  };

  onSendEmailCodeClick = () => {
    const { customer } = this.props;
    const { customerProfile } = this.state;

    this.setState({ isLoading: true });

    const data = { type: NOTIFICATION_CHANNELS_TYPE.EMAIL };
    const emailChannel = customerProfile?.notificationChannels?.find(
      ch => ch.type === NOTIFICATION_CHANNELS_TYPE.EMAIL
    );

    const send = () =>
      resendEmailCode(customer)
        .then(res => {
          const payload = this.normalizePayload(res);

          if (payload?.isError) {
            this.openInfoModal('Error', this.getUserFriendlyError(payload, 'Could not send email code.'));
          } else {
            this.openInfoModal('Info', 'Verification code sent.');
            this.startCountdownFromNextResend('emailRetrySec', payload?.nextResendAt);
          }
        })
        .finally(() => {
          this.setState({ isLoading: false });
          this.setItems();
        });

    if (!emailChannel) {
      createCustomerProfileNotificationChannel(customer, data)
        .then(() => send())
        .catch(err => {
          const serverPayload = err?.response?.data;
          this.openInfoModal('Error', this.getUserFriendlyError(serverPayload, 'Could not create email channel.'));
          this.setState({ isLoading: false });
        });
    } else {
      send().catch(err => {
        const serverPayload = err?.response?.data;
        this.openInfoModal('Error', this.getUserFriendlyError(serverPayload, 'Could not send email code.'));
        this.setState({ isLoading: false });
      });
    }
  };

  onResendPhoneCodeClick = () => {
    const { customer } = this.props;
    const { isEmailVerified } = this.state;

    if (!isEmailVerified) {
      this.openInfoModal('Info', 'Please verify your email first. Then you can request an SMS verification code.');
      return;
    }

    this.setState({ isLoading: true });

    resendPhoneCode(customer)
      .then(res => {
        const payload = this.normalizePayload(res);

        if (payload?.isError) {
          this.openInfoModal('Error', this.getUserFriendlyError(payload, 'Could not resend phone code.'));
        } else {
          this.openInfoModal('Info', 'Verification code sent.');
          this.startCountdownFromNextResend('phoneRetrySec', payload?.nextResendAt);
        }

        this.setState({ isLoading: false });
        this.setItems();
      })
      .catch(err => {
        const retry = err?.response?.headers?.['retry-after'];

        if (retry) {
          this.openInfoModal('Info', `Please wait ${retry} seconds before requesting another code.`);
        } else {
          this.openInfoModal('Error', 'Could not resend phone code.');
        }

        this.setState({ isLoading: false });
      });
  };

  onSendPhoneCodeClick = () => {
    const { customer } = this.props;
    const { isEmailVerified } = this.state;

    if (!isEmailVerified) {
      this.openInfoModal('Info', 'Please verify your email first. Then you can request an SMS verification code.');
      return;
    }

    this.setState({ isLoading: true });

    resendPhoneCode(customer)
      .then(res => {
        const payload = this.normalizePayload(res);

        if (payload?.isError) {
          this.openInfoModal('Error', this.getUserFriendlyError(payload, 'Could not send phone code.'));
        } else {
          this.openInfoModal('Info', 'Verification code sent.');
          this.startCountdownFromNextResend('phoneRetrySec', payload?.nextResendAt);
        }

        this.setState({ isLoading: false });
        this.setItems();
      })
      .catch(err => {
        const serverPayload = err?.response?.data;
        this.openInfoModal('Error', this.getUserFriendlyError(serverPayload, 'Could not send phone code.'));
        this.setState({ isLoading: false });
      });
  };

  onEmailChangeFormSubmit = data => {
    const { customer, updateEmailInProfile } = this.props;

    this.setState({ isLoading: true });

    changeEmail(customer, data.email)
      .then(res => {
        const payload = this.normalizePayload(res);

        if (payload?.isError) {
          this.openInfoModal('Error', this.getUserFriendlyError(payload, 'Could not change email.'));
          this.setState({ isLoading: false });
          return;
        }

        this.setState({
          isLoading: false,
          isEmailChangeModalOpen: false,
          email: data.email,
        });

        this.openInfoModal('Info', 'Email updated. We sent a new verification code.');

        updateEmailInProfile(data.email);
        this.setItems();
      })
      .catch(err => {
        const serverPayload = err?.response?.data;
        this.openInfoModal('Error', this.getUserFriendlyError(serverPayload, 'Could not change email.'));
        this.setState({ isLoading: false });
      });
  };

  onPhoneChangeFormSubmit = data => {
    const { customer, updatePhoneInProfile } = this.props;
    const { isEmailVerified } = this.state;

    this.setState({ isLoading: true });

    const { phone, phoneCode } = data;
    const phoneWithoutFirstZero = getPhoneWithoutFirstZero(phone);
    const phoneToPost = `${phoneCode}${phoneWithoutFirstZero}`;

    changePhone(customer, phoneToPost)
      .then(res => {
        const payload = this.normalizePayload(res);

        if (payload?.isError) {
          this.openInfoModal('Error', this.getUserFriendlyError(payload, 'Could not change phone.'));
          this.setState({ isLoading: false });
          return;
        }

        this.setState({
          isLoading: false,
          isPhoneChangeModalOpen: false,
          phone: phoneToPost,
        });

        // 👇 вот это то, что ты просила
        if (!isEmailVerified) {
          this.openInfoModal(
            'Info',
            'Phone updated. To receive an SMS verification code, please verify your email first.'
          );
        } else {
          this.openInfoModal('Info', 'Phone updated. You can now request a new SMS verification code.');
        }

        updatePhoneInProfile(phoneToPost);
        this.setItems();
      })
      .catch(err => {
        const serverPayload = err?.response?.data;
        this.openInfoModal('Error', this.getUserFriendlyError(serverPayload, 'Could not change phone.'));
        this.setState({ isLoading: false });
      });
  };

  renderEmailChangeModal() {
    const { isEmailChangeModalOpen, email } = this.state;
    return (
      <SimpleModal isOpen={isEmailChangeModalOpen}>
        <ProfileEmailChangeForm
          onCancel={this.onEmailChangeFormCancelClick}
          onSubmit={this.onEmailChangeFormSubmit}
          email={email}
        />
      </SimpleModal>
    );
  }

  renderPhoneChangeModal() {
    const { isPhoneChangeModalOpen, phone } = this.state;
    const { profile } = this.props;
    return (
      <SimpleModal isOpen={isPhoneChangeModalOpen}>
        <ProfilePhoneChangeForm
          onCancel={this.onPhoneChangeFormCancelClick}
          onSubmit={this.onPhoneChangeFormSubmit}
          profile={profile}
        />
      </SimpleModal>
    );
  }

  onStartClick = () => {
    const { updateIsCustomerVerified, history } = this.props;
    updateIsCustomerVerified();
    history.push('/customer');
  };

  render() {
    const {
      emailToken,
      phoneToken,
      isEmailVerified,
      isPhoneVerified,
      isLoading,
      isEmailChangeModalOpen,
      isPhoneChangeModalOpen,
      isInfoModalOpen,
      email,
      phone,
      tokensStatus,
    } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes =
      isEmailChangeModalOpen || isPhoneChangeModalOpen || isInfoModalOpen ? 'mt-5 modal-open mb-5' : 'mt-5 mb-5';

    if (isLoading) {
      return <Loader />;
    }

    const isEmailTokenExist = propz.get(tokensStatus, ['isEmailTokenExist'], false);
    const isPhoneTokenExist = propz.get(tokensStatus, ['isPhoneTokenExist'], false);
    const isPhoneExist = typeof phone !== 'undefined' && phone !== '';

    return (
      <div className={classes}>
        {/*Place for render modal windows*/}
        {this.renderEmailChangeModal()}
        {this.renderPhoneChangeModal()}
        {this.renderInfoModal()}
        <div className="container">
          <div className="row">
            <div className="col-xl-6 offset-xl-3">
              {(!isEmailTokenExist || !isPhoneTokenExist) && (
                <label>We need to verify your email and phone number before you start using our application.</label>
              )}
              <div className={'mt-3 mb-3'}>
                <label>
                  <strong>Email verification code</strong>
                </label>
                {isEmailTokenExist ? (
                  <>
                    <p>We have sent your verification code to: {email}</p>
                    <input
                      type="text"
                      className="form-control mb-3"
                      placeholder="Enter code here"
                      value={emailToken}
                      onChange={this.onEmailTokenChange}
                      disabled={isEmailVerified}
                    />
                    {isEmailVerified === false && (
                      <div className="alert alert-danger">
                        We couldn’t verify that code. Please try again, or request a new one.
                      </div>
                    )}
                    {isEmailVerified && <div className="alert alert-success">Verified</div>}
                    <Button
                      disabled={isEmailVerified}
                      text={'Verify'}
                      onClick={this.onVerifyEmailClick}
                      customClass={'eCustomerVerificationTabButton'}
                    />
                    <Button
                      disabled={isEmailVerified}
                      text={'Change email'}
                      onClick={this.onChangeEmailClick}
                      customClass={'btn-light ml-3 eCustomerVerificationTabButton'}
                    />
                    <Button
                      disabled={isEmailVerified || !!this.state.emailRetrySec}
                      text={
                        this.state.emailRetrySec
                          ? `Resend email code (${this.state.emailRetrySec}s)`
                          : 'Resend email code'
                      }
                      onClick={this.onResendEmailCodeClick}
                      customClass={'btn-light ml-3 eCustomerVerificationTabButton'}
                    />
                  </>
                ) : (
                  <>
                    <label>We have sent your verification code to: {email}</label>
                    <Button
                      text={'Send verification code'}
                      onClick={this.onSendEmailCodeClick}
                      customClass={'eCustomerVerificationTabButton'}
                    />
                    <Button
                      disabled={isEmailVerified}
                      text={'Change email'}
                      onClick={this.onChangeEmailClick}
                      customClass={'btn-light ml-3 eCustomerVerificationTabButton'}
                    />
                  </>
                )}
              </div>
              <div className={'mt-3 mb-3'}>
                <label>
                  <strong>Mobile phone verification code</strong>
                </label>
                {isPhoneExist ? (
                  <p>We have sent your verification code to: {phone}</p>
                ) : (
                  <p>Please enter your mobile phone number and click "Send"</p>
                )}
                {isPhoneTokenExist ? (
                  <>
                    <input
                      type="text"
                      className="form-control mb-3"
                      placeholder="Enter code here"
                      value={phoneToken}
                      onChange={this.onPhoneTokenChange}
                      disabled={isPhoneVerified}
                    />
                    {isPhoneVerified === false && (
                      <div className="alert alert-danger">
                        We couldn’t verify that code. Please try again, or request a new one.
                      </div>
                    )}
                    {isPhoneVerified && <div className="alert alert-success">Verified</div>}
                    <Button
                      disabled={isPhoneVerified}
                      text={'Verify'}
                      onClick={this.onVerifyPhoneClick}
                      customClass={'eCustomerVerificationTabButton'}
                    />
                    <Button
                      disabled={isPhoneVerified}
                      text={isPhoneExist ? 'Change phone' : 'Add phone'}
                      onClick={this.onChangePhoneClick}
                      customClass={'btn-light ml-3 eCustomerVerificationTabButton'}
                    />
                    <Button
                      disabled={isPhoneVerified || !!this.state.phoneRetrySec}
                      text={
                        this.state.phoneRetrySec
                          ? `Resend phone code (${this.state.phoneRetrySec}s)`
                          : 'Resend phone code'
                      }
                      onClick={this.onResendPhoneCodeClick}
                      customClass={'btn-light ml-3 eCustomerVerificationTabButton'}
                    />
                  </>
                ) : (
                  <>
                    <Button
                      disabled={!isPhoneExist}
                      text={'Send verification code'}
                      onClick={this.onSendPhoneCodeClick}
                      customClass={'eCustomerVerificationTabButton'}
                    />
                    <Button
                      text={isPhoneExist ? 'Change phone' : 'Add phone'}
                      onClick={this.onChangePhoneClick}
                      customClass={'btn-light ml-3 eCustomerVerificationTabButton'}
                    />
                  </>
                )}
              </div>
              {isEmailVerified && isPhoneVerified && (
                <Button text={'Start'} onClick={this.onStartClick} customClass={'eCustomerVerificationTabButton'} />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
